/**
 * @class NumModal
 * @extends HTMLElement
 * @description A custom web component for a modal with configurable properties.
 * @attribute {boolean} open - Indicates whether the modal is open.
 * @attribute {string} bg-color - The background color of the modal panel.
 * @attribute {string} z-index - The z-index of the modal.
 * @attribute {string} body-scroll - The body scroll behavior when the modal is open ('hidden', 'scroll', 'auto').
 * @attribute {boolean} full-screen - Indicates whether the modal should be full-screen.
 * @attribute {boolean} header - Indicates whether the modal should have a header.
 */

class NumModal extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._open = false;
    this._panel = null;
    this._overlay = null;
    this._trigger = null;
  }

  connectedCallback() {
    document.body.appendChild(this);
    this._render();
    this._panel = this.shadowRoot.querySelector('.num-modal__panel');
    this._overlay = this.shadowRoot.querySelector('.num-modal__overlay');
    this._attachEventListeners();
    this._updateModalState();
    document.addEventListener('keydown', this._handleKeyDown.bind(this));
    window.addEventListener('resize', this._updateModalState.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._handleKeyDown.bind(this));
    window.removeEventListener('resize', this._updateModalState.bind(this));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this._open = this.hasAttribute('open');
    }
    this._updateModalState();
  }

  open(trigger = null) {
    if (!this._open) {
      this._trigger = trigger;
      this.setAttribute('open', '');
      this._dispatchEvent('onOpen');
      const slot = this._panel.querySelector('slot');
      const assignedElements = slot.assignedElements();
      const focusElement = findFirstElement(assignedElements, 'input:not([type="hidden"])');
      this._panel.addEventListener(
        'transitionend',
        () => {
          if (focusElement) {
            trapFocus(this._panel, focusElement);
          }
        },
        { once: true }
      );
    }
  }

  onClose() {
    close();
  }

  close() {
    if (this._open) {
      this.removeAttribute('open');
      this._dispatchEvent('onClose');
      removeTrapFocus(this._trigger);
    }
  }

  toggle(trigger = null) {
    this._open ? this.close() : this.open(trigger);
  }

  _dispatchEvent(eventName) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true }));
  }

  _handleKeyDown(event) {
    if (event.key === 'Escape' && this.open) {
      this.close();
    }
  }

  _attachEventListeners() {
    this._overlay?.addEventListener('click', () => this.close());
    const closeButton = this.shadowRoot.querySelector('.num-modal__close');
    closeButton?.addEventListener('click', () => this.close());
  }

  _getSize() {
    const sizeAttr = this.getAttribute('size');
    let size = '300px';

    if (sizeAttr) {
      try {
        const sizeObj = JSON.parse(sizeAttr);
        const width = window.innerWidth;

        if (width >= 1200 && sizeObj.lg) {
          size = sizeObj.lg;
        } else if (width >= 992 && sizeObj.md) {
          size = sizeObj.md;
        } else if (width >= 768 && sizeObj.sm) {
          size = sizeObj.sm;
        } else {
          size = sizeObj.default || size;
        }
      } catch (e) {
        size = sizeAttr;
      }
    }

    return size;
  }

  _updateModalState() {
    const bgColor = this.getAttribute('bg-color') || '#fff';
    const fullScreen = this.hasAttribute('full-screen');
    const header = this.getAttribute('header') === 'false' ? false : true;

    if (this._panel && this._overlay) {
      this._panel.style.backgroundColor = bgColor;
      this._panel.style.width = this._getSize();

      if (fullScreen) {
        this._panel.style.width = '100vw';
        this._panel.style.height = '100vh';
        this._panel.style.maxHeight = '100vh';
        this._panel.style.maxWidth = '100vw';
      }

      if (!header) {
        const header = this.shadowRoot.querySelector('.num-modal__header');
        header.style.display = 'none';
      }
    }
    document.body.style.overflow = this._open ? 'hidden' : '';
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
       ${this._getStyles()}
      </style>
      <div class="num-modal__overlay"></div>
      <div class="num-modal__panel">
        <div class="num-modal__header">
            <button class="num-modal__close" aria-label="Close modal">
                <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                    <path d="M10.3283 30.9854L30.9853 10.3284" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10.3283 10.3286L30.9853 30.9856" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
        <div class="num-modal__content">
          <slot></slot>
        </div>
      </div>
    `;
  }

  _getStyles() {
    return `
      :host {
        position: absolute;
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        z-index: 1002;
        pointer-events: none;
      }
      :host([open]) {
        pointer-events: auto;
        width: 100vw;
        height: 100vh;
      }  
      .num-modal__overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: visibility 0s, opacity 0.3s ease;
        z-index: 1001;
        backdrop-filter: blur(6.15px);
      }
      :host([open]) .num-modal__overlay {
        opacity: 1;
      }
      .num-modal__panel {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.9);
        background-color: white;
        border-radius: 0.5rem;
        padding: 1rem;
        opacity: 0;
        transition: opacity 0.3s ease, transform 0.3s ease;
        z-index: 1002;
        max-height: 90vh;
      }
      :host([open]) .num-modal__panel {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      .num-modal__header {
        display: flex;
        justify-content: flex-end;
      }
      .num-modal__close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
      }
    `;
  }
}

customElements.define('num-modal', NumModal);
